import { __awaiter } from './../external/tslib/tslib.es6.js';
import { useState, useEffect, useCallback, useRef } from 'react';

function useMountedState() {
    const mountedRef = useRef(false);
    const get = useCallback(() => mountedRef.current, []);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    return get;
}
const useCopyToClipboard = () => {
    const isMounted = useMountedState();
    const [state, setState] = useState({
        value: undefined,
        error: undefined,
    });
    const copyToClipboard = useCallback((value) => __awaiter(void 0, void 0, void 0, function* () {
        if (!isMounted()) {
            return;
        }
        let normalizedValue;
        try {
            // only strings and numbers casted to strings can be copied to clipboard
            if (typeof value !== 'string' && typeof value !== 'number') {
                const error = new Error(`Cannot copy typeof ${typeof value} to clipboard, must be a string`);
                if (process.env.NODE_ENV === 'development')
                    console.error(error);
                setState({
                    value,
                    error,
                });
                return;
            }
            // empty strings are also considered invalid
            else if (value === '') {
                const error = new Error(`Cannot copy empty string to clipboard.`);
                if (process.env.NODE_ENV === 'development')
                    console.error(error);
                setState({
                    value,
                    error,
                });
                return;
            }
            normalizedValue = value.toString();
            yield navigator.clipboard.writeText(normalizedValue);
            setState({
                value: normalizedValue,
                error: undefined,
            });
        }
        catch (error) {
            setState({
                value: normalizedValue,
                error,
            });
        }
    }), []);
    return [state, copyToClipboard];
};
const useCopyWithDelay = (valueToCopy, delay = 3000) => {
    const [, copyToClipboard] = useCopyToClipboard();
    const [hasCopied, setCopied] = useState(false);
    useEffect(() => {
        let timeoutId;
        if (!hasCopied) {
            timeoutId = setTimeout(() => {
                setCopied(false);
            }, delay);
        }
        return () => {
            clearTimeout(timeoutId);
        };
    }, [hasCopied, delay]);
    const handleCopy = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setCopied(true);
        copyToClipboard(valueToCopy);
    };
    return {
        hasCopied,
        handleCopy,
    };
};

export { useCopyWithDelay };
